// QA-scorecard coaching (Storm 5 Nov 2026, "go coaching design"; design in Drive 03,
// backlog 242). PURE: no React, no fetch, so scripts/verify-qa-coaching.mjs runs it in node.
//
// Each prompt is tied to one question of the shared human scorecard (form 948837df v4) and
// uses the CLIENT'S OWN rule from its QA profile. The profile arrives from
// knktr-agent-coaching {type:"profile"}, which reads it only through the contact binding
// (the contact's own client) and only once approved — so these checks only ever see the
// profile of the client this call is for. With no profile, prompts fall back to the
// scorecard question's own wording and never invent a client rule.
//
// Prompts are fixed text built from the profile: no model call, nothing about the caller
// leaves the browser. Compliance prompts (never-say, privacy, identity) skip the 30 s floor
// (Storm 5 Nov 20:51); the others share it and are capped at MAX_OTHER_PER_CALL.

export const SCORECARD = Object.freeze({
  'never-say': { point: 'Never-say', pts: 15, compliance: true },
  'personal-details': { point: 'Privacy', pts: 10, compliance: true },
  'identity-check': { point: 'Identity check', pts: 5, compliance: true },
  greeting: { point: 'Greeting', pts: 5 },
  'zero-repetition': { point: 'Caller not made to repeat', pts: 5 },
  'no-guessing': { point: 'No guessing', pts: 10 },
  pricing: { point: 'Pricing', pts: 10 },
});
export const MAX_OTHER_PER_CALL = 3;

const agentLines = (v) => (v.transcript || []).filter((l) => l && l.speaker === 'You' && typeof l.text === 'string' && !l.typing);
const custLines = (v) => (v.transcript || []).filter((l) => l && l.speaker === 'Cust' && typeof l.text === 'string' && !l.typing);
const low = (s) => String(s || '').toLowerCase();
const brandOf = (v) => v?.qaProfile?.brandName || 'This client';

export function scorecardLabel(triggerId, brand) {
  const s = SCORECARD[triggerId];
  return s ? `${brand ? `${brand} · ` : ''}${s.point} · ${s.pts} pts` : null;
}

// Never-say: an agent line contains a phrase on THIS client's list. Once per phrase.
export function checkNeverSay(v, state) {
  const list = Array.isArray(v?.qaProfile?.neverSay) ? v.qaProfile.neverSay : [];
  if (!list.length) return null;
  for (const line of agentLines(v)) {
    const t = low(line.text);
    for (const item of list) {
      const p = low(item?.phrase).trim();
      if (!p) continue;
      const re = new RegExp(`(^|[^a-z])${p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z]|$)`);
      const key = `never-say:${p}`;
      if (re.test(t) && !state.firedKeys.has(key)) {
        const instead = item.instead ? ` Say instead: "${item.instead}".` : '';
        return { key, cue: `Avoid "${item.phrase}" on ${brandOf(v)} calls.${instead}` };
      }
    }
  }
  return null;
}

// Identity: the caller raises their booking/account before the agent has asked an ID question.
const ACCOUNT_WORDS = /\b(my (booking|account|event|order|invoice|bill|deposit|quote)|booking (number|reference)|change (my|the) (date|booking)|cancel (my|the))\b/i;
const ID_ASK = /\b(can i (just )?(confirm|verify|check)|could you confirm|what('s| is) (your|the) (full name|name|date of birth|mobile|phone|email|event date|booking)|for security|verify (your|you))\b/i;
export function checkIdentity(v, state) {
  if (state.firedOnce.has('identity-check')) return null;
  const cust = custLines(v);
  const idx = cust.findIndex((l) => ACCOUNT_WORDS.test(l.text));
  if (idx < 0) return null;
  if (agentLines(v).some((l) => ID_ASK.test(l.text))) return null;
  const rule = v?.qaProfile?.idCheck?.verify;
  return { key: 'identity-check', cue: rule
    ? `Before discussing the ${brandOf(v)} booking, verify the caller: ${rule}.`
    : 'Before discussing their account or booking, check the caller’s identity.' };
}

// Greeting: the agent's first line doesn't use THIS client's greeting. Needs the profile.
export function checkGreeting(v, state) {
  if (state.firedOnce.has('greeting')) return null;
  const g = v?.qaProfile?.agentGreeting; const keyPhrase = v?.qaProfile?.greetingKey || v?.qaProfile?.brandName;
  if (!g || !keyPhrase) return null;
  const first = agentLines(v)[0];
  if (!first) return null;
  if (low(first.text).includes(low(keyPhrase))) return { key: 'greeting', silent: true };   // done well: never fire
  return { key: 'greeting', cue: `Open with the ${brandOf(v)} greeting: "${g}"` };
}

// Zero repetition: after an AI hand-over, the agent asks what the call is about.
const ASK_AGAIN = /\b(how can i help|what can i (do|help) (for|with)|what('s| is) the (issue|problem)|what are you calling about|what did you need|how may i help)\b/i;
export function checkRepetition(v, state) {
  if (state.firedOnce.has('zero-repetition')) return null;
  const q = v?.escalation?.handoverQuestion || v?.escalation?.lastQuestion;
  if (!q) return null;
  if (!agentLines(v).some((l) => ASK_AGAIN.test(l.text))) return null;
  return { key: 'zero-repetition', cue: `The caller already told the AI: "${String(q).slice(0, 140)}". Confirm that back instead of asking again.` };
}

// No guessing: the agent hedges an answer (checked on their last two lines). Once per call.
const HEDGE = /\b(i think|i('m| am) not sure|probably|i guess|i believe it'?s|maybe it'?s)\b/i;
export function checkGuessing(v, state) {
  if (state.firedOnce.has('no-guessing')) return null;
  if (!agentLines(v).slice(-2).some((l) => HEDGE.test(l.text))) return null;
  return { key: 'no-guessing', cue: 'If you\u2019re not sure, check Knowledge Search before answering rather than guessing.' };
}

// Pricing: the caller asks about price. Uses THIS client's pricing rule when there is one.
const PRICE_Q = /\b(how much|what('s| is) the (price|cost|rate)|what does it cost|pricing|quote)\b/i;
export function checkPricing(v, state) {
  if (state.firedOnce.has('pricing')) return null;
  if (!custLines(v).some((l) => PRICE_Q.test(l.text))) return null;
  const rule = v?.qaProfile?.pricingRule;
  return { key: 'pricing', cue: rule
    ? `${brandOf(v)} pricing: ${rule}`
    : 'Only quote prices that are in the knowledge base; don’t commit to a figure that isn’t.' };
}

export const QA_CHECKS = [
  ['never-say', checkNeverSay], ['identity-check', checkIdentity], ['greeting', checkGreeting],
  ['zero-repetition', checkRepetition], ['no-guessing', checkGuessing], ['pricing', checkPricing],
];
